import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SharedLogEntry } from "./useSharedProfile";
import "./FieldEditHistory.scss";

type Props = {
  label: string;
  log?: SharedLogEntry[] | null;
  /** Optional: resolve stored ids to option labels for display */
  formatValue?: (value: string) => string;
};

const PenIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const FieldEditHistory: React.FC<Props> = ({ label, log, formatValue }) => {
  const entries = Array.isArray(log) ? log : [];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({
    left: 0,
    top: 0,
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const last = entries[entries.length - 1];
  const show = Boolean(last);

  const clearHide = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const scheduleHide = () => {
    clearHide();
    hideTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const place = () => {
    const btn = btnRef.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const r = btn.getBoundingClientRect();
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left;
    if (left + pw > vw - 12) left = vw - pw - 12;
    if (left < 12) left = 12;
    let top = r.bottom + 8;
    if (top + ph > vh - 12) top = r.top - ph - 8;
    if (top < 12) top = 12;
    setPos({ left, top });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open, entries.length]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  useEffect(
    () => () => {
      clearHide();
    },
    [],
  );

  if (!show || !last) return null;

  const displayValue = (raw: string | undefined) => {
    const text = raw == null || raw === "" ? "—" : String(raw);
    return formatValue ? formatValue(text) : text;
  };

  const rows = [...entries].reverse();

  return (
    <div className="prev audit field-edit-history">
      <button
        ref={btnRef}
        type="button"
        className="prev-btn audit-btn"
        aria-label={`Edit history for ${label}`}
        onMouseEnter={() => {
          clearHide();
          setOpen(true);
        }}
        onMouseLeave={scheduleHide}
        onFocus={() => {
          clearHide();
          setOpen(true);
        }}
        onBlur={scheduleHide}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          clearHide();
          setOpen((v) => !v);
        }}
      >
        <PenIcon />
        <span className="prev-lead">Edited</span>
        <span className="prev-when">{last.date || ""}</span>
        <span className="prev-dot">·</span>
        <span className="prev-val">{last.coach || "Coach"}</span>
        {entries.length > 1 ? (
          <span className="prev-more">{entries.length} edits</span>
        ) : null}
      </button>
      {open
        ? createPortal(
            <div
              ref={popRef}
              className="field-edit-pop show"
              style={{ left: pos.left, top: pos.top }}
              onMouseEnter={clearHide}
              onMouseLeave={scheduleHide}
            >
              <div className="hist-pop-t">
                <PenIcon />
                Edit history · {label}
              </div>
              {rows.map((entry, i) => (
                <div
                  className={`hist-row ${i === 0 ? "latest" : ""}`}
                  key={`${entry.at || entry.date || i}-${i}`}
                >
                  <div className="hist-when">
                    <b>{entry.coach || "Coach"}</b>
                    {entry.role ? ` · ${entry.role}` : ""}
                    <span className="hist-date">{entry.date || ""}</span>
                  </div>
                  <div className="hist-val">{displayValue(entry.value)}</div>
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default FieldEditHistory;
